<script lang="ts" setup>
import {computed, defineAsyncComponent, onMounted, Ref, ref} from "vue";

import linkifyStr from "linkify-string";
import {whatsAppParser} from "@src/helpers/messageHelper";
import ChatMessage from "@src/types/ChatMessage";
import ChatSession from "@src/types/ChatSession";
import MessagePreview from "@src/components/views/HomeView/MainArea/ChatArea/Message/MessagePreview.vue";
import Media from "@src/components/views/HomeView/MainArea/ChatArea/Message/Media.vue";
import MessageContextMenu from "@src/components/views/HomeView/MainArea/ChatArea/Message/MessageContextMenu.vue";
import Receipt from "@src/components/views/HomeView/MainArea/ChatArea/Message/Receipt.vue";
const EmojiPickerPopover = defineAsyncComponent(() => import("@src/components/ui/inputs/EmojiPicker/EmojiPickerPopover.vue"));
import {useMessageReactions} from "@src/composables/useMessageReactions";
import {EllipsisVerticalIcon, FaceSmileIcon, XCircleIcon} from "@heroicons/vue/24/outline";
import IconButton from "@src/components/ui/inputs/IconButton.vue";
import {IRecording} from "@src/types";
const Recording = defineAsyncComponent(() => import("@src/components/views/HomeView/MainArea/ChatArea/Message/Recording.vue"));
import Contact from "@src/types/Contact";
import {useAuthStore} from "@src/stores/auth";
import {useChatSessionStore} from "@src/stores/chatSession";
import systemImage from '@src/assets/images/system_avatar.png';
import CommentMessage from "@src/components/views/HomeView/MainArea/ChatArea/Message/CommentMessage.vue";
import LinkPreview from "@src/components/views/HomeView/MainArea/ChatArea/Message/LinkPreview.vue";
import message from "@src/services/message";
import {Capacitor} from "@capacitor/core";

const props = defineProps<{
    message: ChatMessage;
    followUp: boolean;
    self: boolean;
    divider?: boolean;
    session: ChatSession
    contact: Contact | undefined
}>();

const showContextMenu = ref(false);
const sessionStore = useChatSessionStore()

const contextMenuCoordinations: Ref<{ x: number; y: number }> = ref({
    x: 0,
    y: 0,
});
const authStore = useAuthStore()
const url = ref<string>('');
const caption = ref<string>('');

const showContextMenuAt = (pageX: number, pageY: number) => {
    if (props.message.status === '5') return;

    showContextMenu.value = true;

    const sidebarWidth = 394;
    const inputBarHeight = 80;
    const menuWidth = 220;
    const menuHeight = 200;

    let x = pageX - 400;
    let y = pageY - 80;

    if (x + menuWidth + sidebarWidth > window.innerWidth) {
        x = window.innerWidth - menuWidth - 394;
    }
    if (y + menuHeight + inputBarHeight > window.innerHeight + window.scrollY) {
        y = y - menuHeight;
    }

    contextMenuCoordinations.value = {x, y};
};

const handleShowContextMenu = (event: MouseEvent) => {
    showContextMenuAt(event.pageX, event.pageY);
};

const isNative = Capacitor.isNativePlatform();

const handleDotsMenuOpen = (event: MouseEvent) => {
    if (props.message.status === '5') return;
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 220;
    const menuHeight = 200;

    let x = rect.left;
    let y = rect.bottom + 4;

    if (x + menuWidth > window.innerWidth) x = window.innerWidth - menuWidth - 8;
    if (y + menuHeight > window.innerHeight) y = rect.top - menuHeight - 4;

    showContextMenu.value = true;
    contextMenuCoordinations.value = {x, y};
};

// closes the context menu
const handleCloseContextMenu = () => {
    showContextMenu.value = false;
};

// close the context menu when opening a new one.
const contextConfig = {
    handler: handleCloseContextMenu,
    events: ["contextmenu"],
};

// decide whether to show or hide the avatar next to the image.
const hideAvatar = () => {
    if (props.divider && !props.self) {
        return false;
    } else {
        if (props.followUp) {
            return true;
        }
        if (props.self) {
            return true;
        }
    }
};

const formatDate = () => {
    const createdAt = new Date(props.message.created_at)
    return createdAt.toLocaleString('pt-BR', {hour: '2-digit', minute: '2-digit'});
}

const isActionMessage = computed(() => {
    return (props.message.type === 'warning' && props.message.from !== 'user') || props.message.from === 'system';
});

const ballonClass = () => {
    if (props.message.type === 'warning') {
        return 'rounded-xl mr-4 bg-gray-300 dark:bg-gray-900';
    }
    if (props.message.from === 'system') {
        return 'rounded-xl mr-4 bg-gray-200 dark:bg-gray-500';
    }
    if (props.self) {
        return 'rounded-tl-xl ml-4 order-2 bg-indigo-100 dark:bg-gray-700';
    }
    return 'rounded-tr-xl mr-4 bg-blue-200 dark:bg-gray-600';
}

const {toggleReactionLocal} = useMessageReactions();

const reactionOpen = ref(false);
const reactionPos = ref({x: 0, y: 0});

// abre o picker perto do balão (acima, alinhado ao lado do balão)
function openReactionPicker(e: MouseEvent) {
    e.stopPropagation();
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    // posiciona o picker acima do botão
    reactionPos.value = {
        x: Math.max(8, rect.left - 260), // ajusta pra não sair da tela
        y: Math.max(8, rect.top - 420),
    };

    reactionOpen.value = true;
}

function closeReactionPicker() {
    reactionOpen.value = false;
}

async function onSelectReaction(emoji: string) {
    if (sessionStore.selectedSession?.channel?.type === 'waapi') {
        return
    }
    if (reactionOpen.value) {
        closeReactionPicker();
    }
    await toggleReactionLocal(Number(props.message.id), emoji);
}

function resolveImageUrl(url: string | null | undefined): string {
    if (!url) return ''
    if (/^https?:\/\//.test(url) || url.startsWith('//')) return url
    return `https://sistema.dchat.com.br/storage/${url}`
}

const getProfilePicture = computed(() => {
    if (props.message.from === 'system') {
        return systemImage
    }
    if (!authStore.user) {
        return ''
    }
    if (props.message.sender?.avatar || props.message.sender?.id !== authStore.user.id) {
        return resolveImageUrl(props.message.sender?.avatar)
    }
    return resolveImageUrl(encodeURI(authStore.user?.profile_photo_path ?? authStore.user?.profile_photo_url));
});

function handleLinkClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A') {
        event.preventDefault();
        const href = (target as HTMLAnchorElement).href;
        if (href) window.open(href, '_blank', 'noopener,noreferrer');
    }
}

onMounted(() => {
    if (props.message.type === 'text' || props.message.type === 'warning') {
        url.value = extractURL(props.message.message)
        caption.value = props.message.message.replace(url.value, '').trim()
    }
})

function extractURL(str: string) {
    // Regular expression pattern to match URLs
    var urlPattern = /(https?:\/\/[^\s]+)/;
    // Match the URL pattern in the string
    var match = str.match(urlPattern);
    // If a URL is found, return it, otherwise return null
    return match ? match[0] : '';
}

const isForwardSelected = computed(() => {
    return sessionStore.isMessageSelectedForForward(props.message.id);
});

const canForwardThisMessage = computed(() => {
    return sessionStore.canForwardMessage(props.message);
});

function handleForwardSelection() {
    if (!sessionStore.forwardMode) return;
    if (!canForwardThisMessage.value) return;

    sessionStore.toggleForwardMessage(props.message);
}

function handleMessageClick(event: MouseEvent) {
    if (!sessionStore.forwardMode) return;
    handleForwardSelection();
}

</script>

<template>
    <div
        :id="`message-${props.message.id}`"
        :class="sessionStore.forwardMode && canForwardThisMessage ? 'cursor-pointer' : ''"
        :message-id="props.message.id"
        @click="handleMessageClick"
    >
        <div v-click-outside="contextConfig"
             :class="{ 'justify-end': props.self && props.message.type !== 'warning', 'justify-center': props.message.type === 'warning' }"
             class="xs:mb-6 md:mb-5 flex"
             @click="handleCloseContextMenu"
             @contextmenu.prevent="sessionStore.forwardMode ? null : handleShowContextMenu($event)"
        >

            <!-- dots para mensagem própria: order-1, antes do check -->
            <button
                v-if="isNative && props.self && props.message.from !== 'system' && props.message.type !== 'warning' && props.message.status !== '5'"
                class="self-end mb-0.5 p-1 text-gray-400 dark:text-gray-500 flex-shrink-0 order-first"
                @click.stop="handleDotsMenuOpen"
            >
                <EllipsisVerticalIcon class="w-6 h-6"/>
            </button>

            <div class="flex items-end min-w-0">

                <div v-if="props.self && props.message.from !== 'system' && props.message.type !== 'warning'"
                     class="flex items-end mr-2 order-1">
                    <p class="body-1 text-black/70 dark:text-white/70 whitespace-pre ml-1">{{ formatDate() }}</p>
                </div>

                <!--select da message-->
                <div
                    v-if="sessionStore.forwardMode && canForwardThisMessage"
                    class="flex items-center mr-3 ml-1"
                >
                    <div :class="[
                        'w-6 h-6 rounded-full border-2 flex items-center justify-center transition',
                        isForwardSelected
                        ? 'bg-green-500 border-green-500 text-white'
                        : 'border-gray-400 dark:border-gray-500 bg-white dark:bg-gray-800'
                        ]"
                    >
                        <span v-if="isForwardSelected" class="text-[12px] font-bold">✓</span>
                    </div>
                </div>

                <div class="flex flex-col order-2 min-w-0">

                    <div
                        v-if="(props.message.from === 'user' && props.message.type !== 'warning') || (props.message.from === 'system' && props.message.type !== 'warning')"
                        class="text-xs mb-1 text-right pr-2 text-gray-600 dark:text-white font-semibold"
                    >
                        {{ props.message.sender?.name }}
                    </div>

                    <div
                        :class="[
                            ballonClass(),
                            sessionStore.forwardMode && isForwardSelected
                            ? 'ring-2 ring-green-400 dark:ring-green-500'
                            : ''
                            ]"
                        class="group relative max-w-125 min-w-0 p-5 rounded-b-xl transition duration-500 select-none"
                        style="-webkit-touch-callout: none;"
                    >
                        <div
                            v-if="props.message.from !== 'system'"
                            :class="props.self ? 'left-2' : 'right-2'"
                            class="absolute -top-3"
                        >
                            <IconButton
                                v-if="sessionStore.selectedSession?.channel?.type === 'whatsapp'"
                                class="opacity-0 pointer-events-none cursor-pointer group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity ic-btn-ghost-gray w-7 h-7 bg-white/80 dark:bg-gray-900/80 rounded-full shadow"
                                title="Reagir"
                                @click="openReactionPicker"
                            >
                                <FaceSmileIcon class="w-4 h-4"/>
                            </IconButton>
                        </div>

                        <EmojiPickerPopover
                            v-if="reactionOpen"
                            :open="reactionOpen"
                            :x="reactionPos.x"
                            :y="reactionPos.y"
                            @close="closeReactionPicker"
                            @select="onSelectReaction"
                        />

                        <MessagePreview
                            v-if="props.message.reply_to && props.message.replied_message"
                            :input="false"
                            :message="props.message.replied_message"
                            :self="props.self"
                            :session="props.session"
                            class="mb-5 px-3"/>

                        <div
                            v-if="props.message.message && props.message.status === '5'"
                            class="body-2 italic outline-none text-black opacity-60 dark:text-white dark:opacity-70 break-words flex items-center"
                        >
                            <XCircleIcon class="h-5 w-5 mr-2"></XCircleIcon>
                            <p>Mensagem apagada</p>
                        </div>

                        <CommentMessage
                            v-else-if="props.message.message && props.message.status !== '5' && props.message.type === 'warning' && props.message.from === 'user'"
                            :message="props.message"
                        />
                        <LinkPreview v-else-if="url" :caption="caption" :is-preview="true" :url="url"/>
                        <p
                            v-else-if="props.message.message && props.message.status !== '5' && (props.message.type === 'text' || props.message.type === 'list' || props.message.type === 'chat' || props.message.type === 'warning' || props.message.type === 'ask')"
                            class="body-2 outline-none text-black opacity-60 dark:text-white dark:opacity-70 break-words"
                            tabindex="0"
                            @click="handleLinkClick"
                            v-html="props.message.message.includes('<') ? props.message.message : whatsAppParser(
                                linkifyStr(props.message.message as string, {
                                    className: props.self ? 'text-black opacity-50 dark:text-white' : 'text-indigo-500 dark:text-indigo-300',
                                    format: { url: (value: string) => value.length > 50 ? value.slice(0, 50) + `…` : value },
                                    attributes: {
                                        target: '_blank',
                                    },
                                    rel: 'noopener noreferrer',
                                })
                            )"
                        ></p>

                        <div v-else-if="props.message.message && props.message.type === 'recording'">
                            <Recording
                                :recording="<IRecording><unknown>props.message.message"
                                :self="props.self"
                            />
                        </div>

                        <Media
                            v-else-if="props.message.type !== 'text' && props.message.type !== 'warning' && props.message.type !== 'list' && props.message.type !== 'chat' && props.message.type !== 'ask'"
                            :message="props.message"
                            :preview="false"
                            :self="props.self"
                        />

                        <div v-if="(props.message as any).reactions?.length" class="mt-3 flex flex-wrap gap-2">
                            <button
                                v-for="(r, idx) in (props.message as any).reactions"
                                :key="idx"
                                class="text-xs px-2 py-1 rounded-full cursor-pointer bg-black/5 dark:bg-white/10"
                                title="Alternar reação"
                                @click.stop="onSelectReaction(r.emoji)"
                            >
                                {{ r.emoji ?? r.reaction }}
                            </button>
                        </div>
                        <div v-if="props.message.is_edited && props.message.status !== '5'"
                             class="mt-2 flex flex-wrap gap-2 text-xs text-gray-400">
                            Editada
                        </div>
                    </div>

                    <div
                        v-if="isActionMessage"
                        class="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center"
                    >
                        {{ formatDate() }}
                    </div>
                </div>


                <div v-if="!props.self && props.message.from !== 'system' && props.message.type !== 'warning'"
                     class="flex items-end ml-2 order-3">
                    <p class="body-1 text-black/70 dark:text-white/70 whitespace-pre">{{ formatDate() }}</p>
                </div>

                <!-- dots para mensagem do customer: order-4, depois do horário -->
                <button
                    v-if="isNative && !props.self && props.message.from !== 'system' && props.message.type !== 'warning' && props.message.status !== '5'"
                    class="self-end mb-0.5 p-1 text-gray-400 dark:text-gray-500 flex-shrink-0 order-4"
                    @click.stop="handleDotsMenuOpen"
                >
                    <EllipsisVerticalIcon class="w-6 h-6"/>
                </button>

                <Receipt v-if="props.self && props.message.from !== 'system' && props.message.type !== 'warning'"
                         :state="props.message.status.toString()" class="mr-2"/>
            </div>

            <!--avatar-->
            <div class="ml-4 items-start flex">
                <div
                    v-if="(props.message.from === 'user' && props.message.type !== 'warning') || (props.message.from === 'system' && props.message.type !== 'warning')"
                    :aria-label="props.message.sender?.name || authStore.user?.name"
                    class="outline-none"
                >
                    <div
                        :class="props.message.from === 'system' ? 'w-[2.80rem] h-[2.80rem]' : 'w-[2.25rem] h-[2.25rem]'"
                        :style="{ backgroundImage: `url(${getProfilePicture})` }"
                        class="bg-cover bg-center rounded-full"
                    ></div>
                </div>
            </div>
        </div>
        <MessageContextMenu
            :handleCloseContextMenu="handleCloseContextMenu"
            :isGroup="false"
            :left="contextMenuCoordinations.x"
            :message="props.message"
            :self="props.self"
            :show="showContextMenu"
            :top="contextMenuCoordinations.y"
            @close-context-menu="showContextMenu = false"
        />
    </div>
</template>
